import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateJobPrice, UpdateJobPrice, JobPrice } from '@thro-types/jobmarket';

import { AxiosRequestConfig } from 'axios';

import { getJobPrices, getJobPrice, createJobPrice, updateJobPrice } from 'api/jobmarketService';

/**
 * A custom hook for all JobPrices from the Jobmarket Service.
 *
 * @returns UseQueryResult<JobPrice[], Error>
 */
export const useJobPricesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobPrices', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const jobPrices = getJobPrices(config);
      return jobPrices;
    },
    placeholderData: keepPreviousData,
    initialData: []
  });

  return query;
};

/**
 * A custom hook for one jobPrice from the Jobmarket Service.
 *
 * @returns UseQueryResult<JobPrice[], Error>
 */
export const useJobPriceQuery = (id?: number, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobPrices', id, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      if (!id) {
        return;
      }
      const jobType = getJobPrice(id, config);
      return jobType;
    },
    enabled: !!id
  });

  return query;
};

export const useJobPricesMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['jobPrices'];
  const create = useMutation<JobPrice, Error, CreateJobPrice>({
    mutationFn: async args => {
      return await createJobPrice(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const update = useMutation<JobPrice, Error, { id: number; data: UpdateJobPrice }>({
    mutationFn: async args => {
      return await updateJobPrice(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { update, create } as const;
};
